import {useCallback, useMemo} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useRouteMatch} from "react-router-dom";

import {IMonthCityStats} from "../../../../../api/src/db_queries/month_city_stats_query";
import {IMonthCityTypeStats} from "../../../../../api/src/db_queries/month_city_type_stats_query";
import {getCityMapRegionParams, getCityOption} from "../../../../../config/cities";
import {mapCitySlugToRegionType} from "../../../../../config/regions";
import {getNeighbouringYears, originDate} from "../../../../../utils/dates";
import {useAppDispatch, useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {capitalize} from "../../../../../utils/misc";
import {mapPathnameToOfferType, OfferType} from "../../../common/app/routing/offer_type";
import {RatioBarChartCard} from "../../../components/charts/chart_cards/BarChartCard";
import {RatioColumnStackedChartCard} from "../../../components/charts/chart_cards/ColumnStackedChartCard";
import {RatioDoublePieChartCard} from "../../../components/charts/chart_cards/double_pie_chart_card/DoublePieChartCard";
import {LineColumnChartCard} from "../../../components/charts/chart_cards/LineColumnChartCard";
import {RatioMultilineChartCard} from "../../../components/charts/chart_cards/MultilineChartCard";
import {MultilineTypeChartCard} from "../../../components/charts/chart_cards/MultilineTypeChartCard";
import {Grid} from "../../../components/Grid";
import {withDateGuard} from "../../../components/hoc/withDateGuard";
import {withGTM} from "../../../components/hoc/withGTM";
import {ChartsLayout as Layout} from "../../../components/layout/charts_layout/ChartsLayout";
import {
    clearChartsRegionPoc,
    selectChartsSelectedRegion,
    setChartsSelectedRegion
} from "../../../components/map/redux/map_city_poc_slice";
import {IOsmDistrictPolygon} from "../../../components/map/types/osm_district_polygon";
import {
    buildDistrictDrillDownSelection,
    buildMapParentRegionSnapshot,
    restoreParentRegionSelection
} from "../../../components/map/utils/district_map_drill_down";
import {toCityOutlineOsmMapParams} from "../../../components/map/utils/osm_map_region_params";
import {selectAuthStatus} from "../../auth/auth_slice";
import {selectLatestEntry} from "../../ssr/redux/latest_entry_slice";
import {pageviewAlgolyticsHit} from "../../tracking/algolytics/pageview_hits";
import {usePageview} from "../../tracking/use_pageview";
import {ViewType} from "../../tracking/view_type";
import {
    useGetChartsMonthCityStatsQuery,
    useGetChartsMonthCityTypeStatsQuery,
    useGetChartsMonthDistrictStatsQuery
} from "../api/charts_platform_api";
import {DistrictOsmMapCard} from "../components/DistrictOsmMapCard";
import {useChartsRegionId} from "../hooks/use_charts_region_id";
import {useEffectiveCitySlug} from "../hooks/use_effective_city_slug";
import {useSelectedRegionDisplay} from "../hooks/use_selected_region_display";
import {buildDistrictMapValuesBySlug, resolveMapMetric} from "../utils/build_district_map_values_by_slug";
import {mapRtkQueryStatusToRequestStatus} from "../utils/rtq_status_to_request_status";

export const Offer = withDateGuard(
    withGTM(() => {
        const {path} = useRouteMatch();
        const citySlug = useEffectiveCitySlug();
        const {isKnownCity, regionLabel} = useSelectedRegionDisplay();
        const regionType = mapCitySlugToRegionType(citySlug);
        usePageview(() => {
            pageviewAlgolyticsHit({citySlug, regionType, viewType: ViewType.OFFER_DETAILS});
        });
        const checkOfferType = mapPathnameToOfferType(path);
        const isFlatOfferType = checkOfferType === OfferType.FLAT;
        const {t, i18n} = useTranslation();
        const {currentDate} = useAppSelector(selectLatestEntry);
        const lastMonth = currentDate ?? "";
        const {isLoggedIn, isAuthorized} = useAppSelector(selectAuthStatus);
        const shouldFetch = isLoggedIn && isAuthorized;
        const dispatch = useAppDispatch();
        const chartsSelectedRegion = useAppSelector(selectChartsSelectedRegion);
        const {regionId, districtChartsRegionId, effectiveCitySlug, hasChartsAccess, isRegionIdReady} =
            useChartsRegionId();
        const isPermitted = isAuthorized && hasChartsAccess;
        const chartsCitySlug = effectiveCitySlug ?? citySlug;
        const isAgglomerationView = getCityOption(chartsCitySlug).useAgglomerationApi;
        const routeMapParams = useMemo(
            () => getCityMapRegionParams(chartsCitySlug, regionId ?? undefined),
            [chartsCitySlug, regionId]
        );
        const baseOsmMapParams = useMemo(
            () =>
                chartsSelectedRegion != null
                    ? {
                          regionsMapSlug: chartsSelectedRegion.regionsMapSlug,
                          regionsMapParentId: chartsSelectedRegion.regionsMapParentId,
                          regionsMapRegionId: chartsSelectedRegion.regionsMapRegionId,
                          regionsMapChildType: chartsSelectedRegion.regionsMapChildType
                      }
                    : routeMapParams,
            [chartsSelectedRegion, routeMapParams]
        );
        const hasDistrictStatsMap = baseOsmMapParams.regionsMapParentId != null;
        const skipChartsFetch = !shouldFetch || !isRegionIdReady || regionId == null;

        const {
            data = [],
            isError,
            isSuccess
        } = useGetChartsMonthCityStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                scenario: "offer"
            },
            {skip: skipChartsFetch}
        );

        const {
            data: typeData = [],
            isError: isTypeDataError,
            isSuccess: isTypeDataSuccess
        } = useGetChartsMonthCityTypeStatsQuery(
            {
                region_id: regionId!,
                date_start: originDate,
                date_end: lastMonth,
                scenario: "offer",
                ...(isFlatOfferType && {offer_type: "Mieszkania"})
            },
            {skip: skipChartsFetch}
        );

        const {data: monthDistrictData = []} = useGetChartsMonthDistrictStatsQuery(
            {
                region_id: districtChartsRegionId!,
                date_start: lastMonth,
                date_end: lastMonth,
                scenario: "offer",
                ...(isFlatOfferType ? {offer_type: "Mieszkania"} : {})
            },
            {
                skip: skipChartsFetch || districtChartsRegionId == null || (isAgglomerationView && !hasDistrictStatsMap)
            }
        );

        const isCityMultiDistrictView = baseOsmMapParams.regionsMapParentId != null;
        const showDistrictPolygons = isCityMultiDistrictView && monthDistrictData.length > 0;
        const osmMapParams = useMemo(
            () => (showDistrictPolygons ? baseOsmMapParams : toCityOutlineOsmMapParams(baseOsmMapParams)),
            [baseOsmMapParams, showDistrictPolygons]
        );

        const monthStatsData = useMemo(() => (Array.isArray(data) ? data : []), [data]);
        const monthTypeStatsData = useMemo(() => (Array.isArray(typeData) ? typeData : []), [typeData]);
        const dataStatus = mapRtkQueryStatusToRequestStatus(isError, isSuccess);
        const typeDataStatus = mapRtkQueryStatusToRequestStatus(isTypeDataError, isTypeDataSuccess);
        const monthData = monthStatsData.length > 0 ? [monthStatsData[monthStatsData.length - 1]] : [];
        const flatMonthData = monthTypeStatsData.length > 0 ? [monthTypeStatsData[monthTypeStatsData.length - 1]] : [];

        const districtsMapAvailable = Boolean(
            osmMapParams.regionsMapSlug ||
                osmMapParams.regionsMapParentId != null ||
                osmMapParams.regionsMapRegionId != null
        );
        const lastMapStats = isFlatOfferType ? monthTypeStatsData.at(-1) : monthStatsData.at(-1);
        const districtOfferBySlug = useMemo(
            () =>
                buildDistrictMapValuesBySlug({
                    districtsMapAvailable,
                    monthDistrictData,
                    chartsCitySlug,
                    districtMetric: (d) => d.available_offers,
                    cityLevelMetric: resolveMapMetric(lastMapStats?.available_offers),
                    cityLevelStatsRecord: lastMapStats,
                    extraSlugKeys: [osmMapParams.regionsMapSlug, chartsSelectedRegion?.slug]
                }),
            [
                chartsCitySlug,
                chartsSelectedRegion?.slug,
                districtsMapAvailable,
                lastMapStats,
                monthDistrictData,
                osmMapParams.regionsMapSlug
            ]
        );
        const districtDrillDownEnabled = showDistrictPolygons && monthDistrictData.length > 0;
        const districtStatsCitySlug = getCityOption(chartsCitySlug).requestCitySlug;
        const mapParentRegion = chartsSelectedRegion?.parentRegion;

        const handleDistrictPolygonClick = useCallback(
            (district: IOsmDistrictPolygon) => {
                if (
                    districtChartsRegionId == null ||
                    baseOsmMapParams.regionsMapParentId == null ||
                    chartsCitySlug == null
                ) {
                    return;
                }

                const parentLabel = chartsSelectedRegion?.label ?? t(`city.${citySlug}.nominative`);
                const parentSlug = chartsSelectedRegion?.slug ?? baseOsmMapParams.regionsMapSlug ?? "";

                dispatch(
                    setChartsSelectedRegion(
                        buildDistrictDrillDownSelection(
                            district,
                            chartsCitySlug,
                            buildMapParentRegionSnapshot({
                                isRouteCity: chartsSelectedRegion == null,
                                regionId: districtChartsRegionId,
                                slug: parentSlug,
                                label: parentLabel,
                                citySlug: chartsSelectedRegion?.citySlug ?? chartsCitySlug,
                                hasChartsAccess,
                                mapParams: baseOsmMapParams
                            })
                        )
                    )
                );

                window.userengage?.("event.[BD]_district_click", {
                    city: chartsCitySlug,
                    district: district.slug_district ?? district.slug
                });
            },
            [baseOsmMapParams, chartsCitySlug, chartsSelectedRegion, citySlug, dispatch, districtChartsRegionId, t]
        );

        const handleBackToMapParent = useCallback(() => {
            const parent = chartsSelectedRegion?.parentRegion;
            if (!parent) {
                return;
            }

            if (parent.isRouteCity) {
                dispatch(clearChartsRegionPoc());
            } else {
                dispatch(setChartsSelectedRegion(restoreParentRegionSelection(parent)));
            }
        }, [chartsSelectedRegion, dispatch]);
        const doublePieChartLabels = getNeighbouringYears(new Date(lastMonth), 5);

        return (
            <Layout>
                <Grid type={districtsMapAvailable ? "subpage" : "subpageMapless"}>
                    <LineColumnChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart1"
                        chartId="offer-chart1"
                        title={
                            <Trans>
                                {t(isFlatOfferType ? "offer.chart1.title_flats" : "offer.chart1.title_properties")}
                            </Trans>
                        }
                        helper={<Trans>{t("offer.chart1.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={dataStatus}
                        columnFields={["sold", "added"]}
                        lineField="available_offers"
                        lineAxisMinValue={0}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <RatioMultilineChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart2"
                        chartId="offer-chart2"
                        title={<Trans>{t("offer.chart2.title")}</Trans>}
                        helper={<Trans>{t("offer.chart2.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={dataStatus}
                        fields={[
                            "available_studio",
                            "available_2_rooms",
                            "available_3_rooms",
                            "available_4_plus_rooms",
                            ...(isFlatOfferType ? [] : ["available_house"])
                        ]}
                        unit={` ${t("common.offer", {count: 5})}`}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <RatioMultilineChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart3"
                        chartId="offer-chart3"
                        title={<Trans>{t("offer.chart3.title")}</Trans>}
                        helper={<Trans>{t("offer.chart3.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={dataStatus}
                        fields={
                            isFlatOfferType
                                ? [
                                      "available_area_to_30m2",
                                      "available_area_between_30_and_50m2",
                                      "available_area_between_50_and_70m2",
                                      "available_area_between_70_and_100m2",
                                      "available_area_between_100_and_120m2",
                                      "available_area_above_120m2"
                                  ]
                                : [
                                      "active_area_to_30m2",
                                      "active_area_between_30_and_50m2",
                                      "active_area_between_50_and_70m2",
                                      "active_area_between_70_and_100m2",
                                      "active_area_between_100_and_120m2",
                                      "active_area_above_120m2"
                                  ]
                        }
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <LineColumnChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart4"
                        chartId="offer-chart4"
                        title={<Trans>{t("offer.chart4.title")}</Trans>}
                        helper={<Trans>{t("offer.chart4.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={dataStatus}
                        columnFields={[]}
                        lineField="percentage_offers_put_into_service"
                        lineAxisMinValue={0}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    {/*  TODO add houses on offertype.estates?*/}
                    <MultilineTypeChartCard
                        area="chart5"
                        chartId="offer-chart5"
                        title={<Trans>{t("offer.chart5.title")}</Trans>}
                        helper={<Trans>{t("offer.chart5.description")}</Trans>}
                        data={monthTypeStatsData}
                        dataStatus={typeDataStatus}
                        fields={["avg_area"]}
                        unit={` ${t("common.m2")}`}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                        minValue={0}
                        isFlatType={isFlatOfferType}
                    />
                    <RatioDoublePieChartCard<IMonthCityStats>
                        area="chart6"
                        chartId="offer-chart6"
                        title={<Trans>{t("offer.chart6.title")}</Trans>}
                        helper={<Trans>{t("offer.chart6.description")}</Trans>}
                        data={monthData}
                        dataStatus={dataStatus}
                        withSharedLegend
                        chart1InnerTitle={capitalize(t("common.flats.nominative"))}
                        chart1Fields={[
                            "available_apartments_delivery_deadline_before_previous_year",
                            "available_apartments_delivery_deadline_in_previous_year",
                            "available_apartments_delivery_deadline_in_current_year",
                            "available_apartments_delivery_deadline_in_next_year",
                            "available_apartments_delivery_deadline_after_next_year"
                        ]}
                        chart1TooltipLabel="flat"
                        chart2InnerTitle={capitalize(t("common.houses.nominative"))}
                        chart2Fields={[
                            "available_houses_delivery_deadline_before_previous_year",
                            "available_houses_delivery_deadline_in_previous_year",
                            "available_houses_delivery_deadline_in_current_year",
                            "available_houses_delivery_deadline_in_next_year",
                            "available_houses_delivery_deadline_after_next_year"
                        ]}
                        chart2TooltipLabel="house"
                        sharedLegendLabels={doublePieChartLabels}
                        fieldsLabels={doublePieChartLabels}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                        isFlatOfferType={isFlatOfferType}
                    />
                    <RatioBarChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart7"
                        chartId="offer-chart7"
                        title={<Trans>{t("offer.chart7.title")}</Trans>}
                        helper={<Trans>{t("offer.chart7.description")}</Trans>}
                        data={isFlatOfferType ? flatMonthData : monthData}
                        dataStatus={dataStatus}
                        fields={[
                            "available_offers_to_6k",
                            "available_offers_between_6k_and_7k",
                            "available_offers_between_7k_and_8k",
                            "available_offers_between_8k_and_9k",
                            "available_offers_between_9k_and_10k",
                            "available_offers_between_10k_and_12k",
                            "available_offers_between_12k_and_15k",
                            "available_offers_between_15k_and_20k",
                            "available_offers_above_20k"
                        ]}
                        height={250}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <RatioMultilineChartCard<IMonthCityStats | IMonthCityTypeStats>
                        area="chart8"
                        chartId="offer-chart8"
                        title={<Trans>{t("offer.chart8.title")}</Trans>}
                        helper={<Trans>{t("offer.chart8.description")}</Trans>}
                        data={isFlatOfferType ? monthTypeStatsData : monthStatsData}
                        dataStatus={dataStatus}
                        fields={[
                            "available_investment_type_intimate",
                            "available_investment_type_small",
                            "available_investment_type_medium",
                            "available_investment_type_large",
                            "available_investment_type_x_large",
                            ...(isFlatOfferType ? [] : ["available_investment_type_house"])
                        ]}
                        unit={` ${t("common.investment", {count: 5})}`}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />
                    <RatioColumnStackedChartCard
                        area="chart9"
                        chartId="offer-chart9"
                        title={<Trans>{t("offer.chart9.title")}</Trans>}
                        helper={<Trans>{t("offer.chart9.description")}</Trans>}
                        data={isFlatOfferType ? flatMonthData : monthData}
                        dataStatus={dataStatus}
                        categories={[
                            "investment_type_intimate",
                            "investment_type_small",
                            "investment_type_medium",
                            "investment_type_large",
                            "investment_type_x_large",
                            ...(isFlatOfferType ? [] : ["investment_type_house"])
                        ]}
                        fieldGroups={[
                            [
                                "available_investment_type_intimate_offer_studio",
                                "available_investment_type_small_offer_studio",
                                "available_investment_type_medium_offer_studio",
                                "available_investment_type_large_offer_studio",
                                "available_investment_type_x_large_offer_studio",
                                ...(isFlatOfferType ? [] : ["available_houses_to_100m2"])
                            ],
                            [
                                "available_investment_type_intimate_offer_2_rooms",
                                "available_investment_type_small_offer_2_rooms",
                                "available_investment_type_medium_offer_2_rooms",
                                "available_investment_type_large_offer_2_rooms",
                                "available_investment_type_x_large_offer_2_rooms",
                                ...(isFlatOfferType ? [] : ["available_houses_between_100_and_150m2"])
                            ],
                            [
                                "available_investment_type_intimate_offer_3_rooms",
                                "available_investment_type_small_offer_3_rooms",
                                "available_investment_type_medium_offer_3_rooms",
                                "available_investment_type_large_offer_3_rooms",
                                "available_investment_type_x_large_offer_3_rooms",
                                ...(isFlatOfferType ? [] : ["available_houses_between_150_and_200m2"])
                            ],
                            [
                                "available_investment_type_intimate_offer_4_plus_rooms",
                                "available_investment_type_small_offer_4_plus_rooms",
                                "available_investment_type_medium_offer_4_plus_rooms",
                                "available_investment_type_large_offer_4_plus_rooms",
                                "available_investment_type_x_large_offer_4_plus_rooms",
                                ...(isFlatOfferType ? [] : ["available_houses_between_200_and_250m2"])
                            ],
                            ["", "", "", "", "", ...(isFlatOfferType ? [] : ["available_houses_above_250m2"])]
                        ]}
                        minValue={1}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                    />

                    <DistrictOsmMapCard
                        chartId="offer-map"
                        title={
                            <Trans>
                                {isKnownCity
                                    ? t("offer.map.title", {city: chartsCitySlug})
                                    : t("offer.map.title_region", {region: regionLabel})}
                            </Trans>
                        }
                        helper={t("offer.map.description")}
                        visible={districtsMapAvailable}
                        sticky
                        citySlug={chartsCitySlug}
                        regionId={regionId}
                        regionsMapSlug={osmMapParams.regionsMapSlug}
                        regionsMapParentId={osmMapParams.regionsMapParentId}
                        regionsMapChildType={osmMapParams.regionsMapChildType}
                        regionsMapRegionId={osmMapParams.regionsMapRegionId}
                        districtsMapAvailable={districtsMapAvailable}
                        districtValuesBySlug={districtOfferBySlug}
                        districtStatsCitySlug={districtStatsCitySlug}
                        legendType="quantitative"
                        legendColors={["#C0F2DC", "#A4F7CC", "#60F1C8", "#2CD9C5", "#20AC9C"]}
                        metricLabel={t("fields.offer", {defaultValue: "Oferta"})}
                        locale={i18n.language}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                        cityOutlineMetric={!showDistrictPolygons}
                        districtDrillDownEnabled={districtDrillDownEnabled}
                        onDistrictPolygonClick={handleDistrictPolygonClick}
                        onBackToParentRegion={mapParentRegion ? handleBackToMapParent : undefined}
                        backToParentLabel={mapParentRegion ? `← ${mapParentRegion.label}` : undefined}
                    />
                </Grid>
            </Layout>
        );
    })
);
